import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  applyActionCode,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  createUserWithEmailAndPassword,
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
  type Auth,
  type User,
} from 'firebase/auth';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const requiredKeys = [
  'apiKey',
  'authDomain',
  'projectId',
  'storageBucket',
  'messagingSenderId',
  'appId',
] as const;

for (const key of requiredKeys) {
  if (!firebaseConfig[key]) {
    throw new Error(`Missing Firebase config: ${key}`);
  }
}

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const storage = getStorage(app);

void setPersistence(auth, browserLocalPersistence).catch(() => {
  // Persistence may already be set or unavailable (private mode); ignore.
});

export function getFirebaseApp(): FirebaseApp {
  return app;
}

export function getFirebaseAuth(): Auth {
  return auth;
}

/**
 * Google sign-in. Prefers popup (works reliably in dev, survives React StrictMode
 * double-mount). Falls back to a full-page redirect if the popup is blocked or the
 * browser's Cross-Origin-Opener-Policy prevents it from posting the result back.
 */
export async function firebaseSignInWithGoogle() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  try {
    await signInWithPopup(getFirebaseAuth(), provider, browserPopupRedirectResolver);
  } catch (err: unknown) {
    const code =
      err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : '';
    if (
      code === 'auth/popup-blocked' ||
      code === 'auth/popup-closed-by-user' ||
      code === 'auth/cancelled-popup-request' ||
      code === 'auth/operation-not-supported-in-this-environment'
    ) {
      await signInWithRedirect(getFirebaseAuth(), provider);
      return;
    }
    throw err;
  }
}

/** Run on app boot to drain any pending redirect result (fallback path). Safe no-op otherwise. */
export async function consumeFirebaseRedirectResult(): Promise<void> {
  try {
    await getRedirectResult(getFirebaseAuth());
  } catch {
    // e.g. auth/operation-not-allowed, cancelled — ignore
  }
}

export async function firebaseSignInWithEmail(email: string, password: string) {
  await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
}

export async function firebaseRegisterWithEmail(
  name: string,
  email: string,
  password: string,
) {
  const cred = await createUserWithEmailAndPassword(getFirebaseAuth(), email, password);
  await updateProfile(cred.user, { displayName: name });
}

export async function firebaseApplyEmailVerification(oobCode: string): Promise<void> {
  await applyActionCode(getFirebaseAuth(), oobCode);
}

export async function firebaseSendPasswordReset(email: string, languageCode?: string) {
  const fbAuth = getFirebaseAuth();
  // Firebase renders its own password-reset email server-side and only
  // localizes it when `languageCode` is set. Without this, a Polish user
  // always receives the English template. Fall back to the device language.
  const normalized = languageCode?.slice(0, 2).toLowerCase();
  if (normalized) {
    fbAuth.languageCode = normalized;
  } else {
    fbAuth.useDeviceLanguage();
  }
  await sendPasswordResetEmail(fbAuth, email);
}

export async function firebaseSignOut() {
  await signOut(getFirebaseAuth());
}

export function subscribeAuth(cb: (user: User | null) => void) {
  return onAuthStateChanged(getFirebaseAuth(), cb);
}

const EXTERNAL_AVATAR_HOSTS = [
  'googleusercontent.com',
  'graph.facebook.com',
  'platform-lookaside.fbsbx.com',
];

export function isExternalProviderAvatar(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const { hostname } = new URL(url);
    return EXTERNAL_AVATAR_HOSTS.some((host) => hostname.endsWith(host));
  } catch {
    return false;
  }
}
